// Mesh Network Page - Full CRUD with State Management
// AI creates semantic connections between tabs and visualizes relationships

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Network, RefreshCw, Search, Lightbulb, Globe,
  Plus, Trash2, Edit2, X, Save, Link2,
} from 'lucide-react';
import apiClient from '../services/api-client';
import { Button } from '../components/ui/Button';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { PageLayout } from '@/components/layout/PageLayout';
import { MeshGraphCanvas } from '@/components/mesh/MeshGraphCanvas';

interface MeshNode {
  id: string;
  label: string;
  url?: string;
  favicon?: string;
  topics: string[];
  sentiment?: number;
  groupId?: string;
  x?: number;
  y?: number;
  metadata?: Record<string, any>;
  createdAt: string;
  lastAccessedAt: string;
}

interface MeshEdge {
  id: string;
  source: string;
  target: string;
  strength: number;
  type: 'semantic' | 'temporal' | 'referential' | 'contextual';
  label?: string;
  bidirectional?: boolean;
}

interface Insight {
  id: string;
  sourceId: string;
  targetId: string;
  sourceLabel: string;
  targetLabel: string;
  insight: string;
  type: string;
}

const edgeTypeColors = {
  semantic: '#8B5CF6',
  temporal: '#10B981',
  referential: '#3B82F6',
  contextual: '#F59E0B',
};

const edgeTypeLabels = {
  semantic: 'Content similarity',
  temporal: 'Time-based',
  referential: 'Direct reference',
  contextual: 'Related context',
};

function normalizeMeshNode(raw: Record<string, unknown>): MeshNode {
  return {
    id: String(raw.id ?? ''),
    label: String(raw.label ?? ''),
    url: raw.url != null ? String(raw.url) : undefined,
    favicon: raw.favicon != null ? String(raw.favicon) : undefined,
    topics: Array.isArray(raw.topics) ? raw.topics.map(String) : [],
    sentiment: raw.sentiment != null ? Number(raw.sentiment) : undefined,
    groupId: raw.groupId != null ? String(raw.groupId) : raw.group_id != null ? String(raw.group_id) : undefined,
    x: raw.x != null ? Number(raw.x) : undefined,
    y: raw.y != null ? Number(raw.y) : undefined,
    metadata: raw.metadata as Record<string, unknown> | undefined,
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    lastAccessedAt: String(raw.lastAccessedAt ?? raw.last_accessed_at ?? ''),
  };
}

function normalizeMeshEdge(raw: Record<string, unknown>): MeshEdge {
  const type = String(raw.type ?? 'semantic');
  const edgeType = ['semantic', 'temporal', 'referential', 'contextual'].includes(type)
    ? (type as MeshEdge['type'])
    : 'semantic';
  return {
    id: String(raw.id ?? ''),
    source: String(raw.source ?? raw.source_id ?? ''),
    target: String(raw.target ?? raw.target_id ?? ''),
    strength: Number(raw.strength ?? 0),
    type: edgeType,
    label: raw.label != null ? String(raw.label) : undefined,
    bidirectional: Boolean(raw.bidirectional),
  };
}

function normalizeInsight(raw: Record<string, unknown>): Insight {
  return {
    id: String(raw.id ?? ''),
    sourceId: String(raw.sourceId ?? raw.source_id ?? ''),
    targetId: String(raw.targetId ?? raw.target_id ?? ''),
    sourceLabel: String(raw.sourceLabel ?? raw.source_label ?? ''),
    targetLabel: String(raw.targetLabel ?? raw.target_label ?? ''),
    insight: String(raw.insight ?? ''),
    type: String(raw.type ?? ''),
  };
}

const safeStr = (val: any, fallback = ''): string => {
  if (val == null) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  return JSON.stringify(val);
};

function shortTabLabel(label: string, max = 24): string {
  const base = (label.split(' - ')[0] ?? label).trim();
  if (base.length <= max) return base;
  return `${base.slice(0, max - 1)}…`;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 items-start text-sm">
      <span className="text-khiimori-text-muted pt-0.5 shrink-0">{label}</span>
      <div className="text-khiimori-text-primary min-w-0">{children}</div>
    </div>
  );
}

function MetricBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-3 mt-2">
      <div className="flex-1 h-2 bg-khiimori-bg-elevated/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-success-500 to-success-400 rounded-full"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <span className="text-khiimori-text-primary text-sm tabular-nums w-10 text-right shrink-0 leading-none">
        {pct}%
      </span>
    </div>
  );
}

export default function MeshNetworkPage() {
  // State management
  const [nodes, setNodes] = useState<MeshNode[]>([]);
  const [edges, setEdges] = useState<MeshEdge[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [selectedNode, setSelectedNode] = useState<MeshNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<MeshEdge | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [layout, setLayout] = useState<'force' | 'hierarchical' | 'radial'>('hierarchical');
  const [showInsights, setShowInsights] = useState(true);

  // Modal states
  const [showCreateNodeModal, setShowCreateNodeModal] = useState(false);
  const [showEditNodeModal, setShowEditNodeModal] = useState(false);
  const [showCreateEdgeModal, setShowCreateEdgeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<'node' | 'edge' | null>(null);

  // Form states
  const [nodeFormData, setNodeFormData] = useState({
    label: '',
    url: '',
    topics: [] as string[],
    sentiment: 0.7,
  });
  const [edgeFormData, setEdgeFormData] = useState({
    sourceId: '',
    targetId: '',
    strength: 0.7,
    type: 'semantic' as MeshEdge['type'],
    label: '',
  });
  const [topicInput, setTopicInput] = useState('');

  const loadMeshData = useCallback(async (options?: { sync?: boolean }) => {
    try {
      setIsLoading(true);
      setError(null);
      const payload = options?.sync === false
        ? await apiClient.getMeshStats()
        : await apiClient.syncMeshFromTabs();
      setNodes(payload.nodes.map((item) => normalizeMeshNode(item)));
      setEdges(payload.edges.map((item) => normalizeMeshEdge(item)));
      setInsights(payload.insights.map((item) => normalizeInsight(item)));
    } catch (err: any) {
      try {
        const visualization = await apiClient.getMeshVisualization();
        setNodes(visualization.nodes.map((item) => normalizeMeshNode(item)));
        setEdges(visualization.edges.map((item) => normalizeMeshEdge(item)));
        const stats = await apiClient.getMeshStats();
        setInsights(stats.insights.map((item) => normalizeInsight(item)));
      } catch {
        setError(err.message || 'Failed to fetch mesh network');
        setNodes([]);
        setEdges([]);
        setInsights([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeshData();
  }, [loadMeshData]);

  // CRUD Operations
  const createNode = async () => {
    try {
      const created = await apiClient.createNode({
        label: nodeFormData.label,
        url: nodeFormData.url || undefined,
        topics: nodeFormData.topics,
        sentiment: nodeFormData.sentiment,
      });
      setNodes(prev => [...prev, normalizeMeshNode(created)]);

      setShowCreateNodeModal(false);
      resetNodeForm();
    } catch (err: any) {
      setError(err.message || 'Failed to create node');
    }
  };

  const updateNode = async (id: string) => {
    try {
      const updated: Partial<MeshNode> = {
        label: nodeFormData.label,
        topics: nodeFormData.topics,
        sentiment: nodeFormData.sentiment,
      };

      const result = await apiClient.updateNode(id, updated);
      setNodes(prev => prev.map(n => n.id === id ? normalizeMeshNode(result) : n));

      setShowEditNodeModal(false);
      setSelectedNode(null);
      resetNodeForm();
    } catch (err: any) {
      setError(err.message || 'Failed to update node');
    }
  };

  const deleteNode = async (id: string) => {
    try {
      await apiClient.deleteNode(id);

      setNodes(prev => prev.filter(n => n.id !== id));
      setEdges(prev => prev.filter(e => e.source !== id && e.target !== id));

      if (selectedNode?.id === id) {
        setSelectedNode(null);
      }
      setShowDeleteConfirm(null);
      setDeleteType(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete node');
    }
  };

  const createEdge = async () => {
    try {
      const created = await apiClient.createMeshEdge({
        source: edgeFormData.sourceId,
        target: edgeFormData.targetId,
        strength: edgeFormData.strength,
        type: edgeFormData.type,
        label: edgeFormData.label || undefined,
      });
      setEdges(prev => [...prev, normalizeMeshEdge(created)]);
      setShowCreateEdgeModal(false);
      resetEdgeForm();
      const stats = await apiClient.getMeshStats();
      setInsights(stats.insights.map((item) => normalizeInsight(item)));
    } catch (err: any) {
      setError(err.message || 'Failed to create edge');
    }
  };

  const deleteEdge = async (id: string) => {
    try {
      await apiClient.deleteMeshEdge(id);
      setEdges(prev => prev.filter(e => e.id !== id));
      setShowDeleteConfirm(null);
      setDeleteType(null);
      if (selectedEdge?.id === id) {
        setSelectedEdge(null);
      }
      const stats = await apiClient.getMeshStats();
      setInsights(stats.insights.map((item) => normalizeInsight(item)));
    } catch (err: any) {
      setError(err.message || 'Failed to delete edge');
    }
  };

  const handleDelete = () => {
    if (deleteType === 'node' && showDeleteConfirm) {
      deleteNode(showDeleteConfirm);
    } else if (deleteType === 'edge' && showDeleteConfirm) {
      deleteEdge(showDeleteConfirm);
    }
  };

  const resetNodeForm = () => {
    setNodeFormData({
      label: '',
      url: '',
      topics: [],
      sentiment: 0.7,
    });
    setTopicInput('');
  };

  const resetEdgeForm = () => {
    setEdgeFormData({
      sourceId: '',
      targetId: '',
      strength: 0.7,
      type: 'semantic',
      label: '',
    });
  };

  const handleAddTopic = () => {
    if (topicInput.trim() && !nodeFormData.topics.includes(topicInput.trim())) {
      setNodeFormData(prev => ({
        ...prev,
        topics: [...prev.topics, topicInput.trim()],
      }));
      setTopicInput('');
    }
  };

  const handleRemoveTopic = (topic: string) => {
    setNodeFormData(prev => ({
      ...prev,
      topics: prev.topics.filter(t => t !== topic),
    }));
  };

  const openEditNodeModal = (node: MeshNode) => {
    setSelectedNode(node);
    setNodeFormData({
      label: node.label,
      url: node.url || '',
      topics: node.topics,
      sentiment: node.sentiment || 0.7,
    });
    setShowEditNodeModal(true);
  };

  const openDeleteConfirm = (id: string, type: 'node' | 'edge') => {
    setShowDeleteConfirm(id);
    setDeleteType(type);
  };

  const getEdgeStrengthLabel = (strength: number) => {
    if (strength >= 0.8) return 'Strong';
    if (strength >= 0.5) return 'Medium';
    return 'Weak';
  };

  return (
    <PageLayout
      title="Mesh Network"
      description="AI-powered semantic connections and relationship visualization"
      icon={Network}
    >
      {/* Header */}
      <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border-b border-khiimori-border/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => loadMeshData()}
                variant="glass"
                leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
                disabled={isLoading}
              >
                Sync Tabs
              </Button>
              <Button
                onClick={() => { resetNodeForm(); setShowCreateNodeModal(true); }}
                variant="primary"
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add Node
              </Button>
              <Button
                onClick={() => { resetEdgeForm(); setShowCreateEdgeModal(true); }}
                variant="glass"
                leftIcon={<Link2 className="w-4 h-4" />}
              >
                Add Connection
              </Button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Nodes', value: nodes.length },
              { label: 'Connections', value: edges.length },
              {
                label: 'Avg Strength',
                value: edges.length > 0
                  ? `${Math.round(edges.reduce((sum, e) => sum + e.strength, 0) / edges.length * 100)}%`
                  : '0%',
              },
              { label: 'Insights', value: insights.length },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-khiimori-bg-elevated/5 rounded-xl px-4 py-3 flex flex-col gap-1"
              >
                <span className="text-khiimori-text-muted text-xs font-medium uppercase tracking-wide leading-none">
                  {stat.label}
                </span>
                <span className="text-khiimori-text-primary text-xl font-bold tabular-nums leading-tight">
                  {stat.value}
                </span>
              </div>
            ))}
          </div>

          {/* Control Bar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-khiimori-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tabs…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-khiimori-bg-elevated/5 border border-khiimori-border/10 text-khiimori-text-primary placeholder:text-khiimori-text-muted focus:outline-none focus:border-primary-500/40"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-khiimori-text-muted text-sm">Filter:</span>
              {['semantic', 'temporal', 'referential', 'contextual'].map(type => (
                <Button
                  key={type}
                  onClick={() => setFilterType(filterType === type ? null : type)}
                  variant={filterType === type ? 'primary' : 'glass'}
                  size="sm"
                  className="inline-flex items-center"
                >
                  <span className="inline-flex items-center gap-1.5 leading-none">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: edgeTypeColors[type as keyof typeof edgeTypeColors] }} />
                    {type}
                  </span>
                </Button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button
                onClick={() => setShowInsights(!showInsights)}
                variant={showInsights ? 'primary' : 'glass'}
                size="sm"
                className="inline-flex items-center justify-center gap-1.5"
                leftIcon={<Lightbulb className="w-4 h-4" />}
              >
                Insights
              </Button>
              <div className="flex items-center gap-1 bg-khiimori-bg-elevated/5 rounded-lg p-1">
                {(['force', 'hierarchical', 'radial'] as const).map(l => (
                  <Button
                    key={l}
                    onClick={() => setLayout(l)}
                    variant={layout === l ? 'primary' : 'ghost'}
                    size="sm"
                  >
                    {l}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-error-500/20 border border-error-500/50 rounded-xl text-error-400 flex items-center justify-between"
          >
            <span>{error}</span>
            <Button onClick={() => setError(null)} variant="ghost" size="sm" aria-label="Dismiss error">
              <X className="w-4 h-4" />
            </Button>
          </motion.div>
        )}

        <div className="grid grid-cols-4 gap-6">
          {/* Network Visualization */}
          <div className="col-span-3 relative bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl border border-khiimori-border/10 overflow-hidden min-h-[560px]">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-khiimori-bg-primary/40">
                <RefreshCw className="w-8 h-8 text-primary-400 animate-spin" />
              </div>
            )}
            {nodes.length === 0 && !isLoading ? (
              <div className="flex flex-col items-center justify-center h-[560px] text-center px-8">
                <Network className="w-12 h-12 text-khiimori-text-muted mb-4" />
                <p className="text-khiimori-text-secondary mb-2">No mesh nodes yet</p>
                <p className="text-khiimori-text-muted text-sm mb-4">
                  Sync your open browser tabs to build the relationship graph, or add nodes manually.
                </p>
                <Button onClick={() => loadMeshData()} variant="primary" leftIcon={<RefreshCw className="w-4 h-4" />}>
                  Sync from tabs
                </Button>
              </div>
            ) : (
              <MeshGraphCanvas
                className="h-[560px]"
                nodes={nodes}
                edges={edges}
                layout={layout}
                filterType={filterType}
                searchQuery={searchQuery}
                selectedNodeId={selectedNode?.id}
                selectedEdgeId={selectedEdge?.id}
                onNodeClick={(id) => {
                  const node = nodes.find((n) => n.id === id) ?? null;
                  setSelectedNode(node);
                  setSelectedEdge(null);
                }}
                onEdgeClick={(id) => {
                  const edge = edges.find((e) => e.id === id) ?? null;
                  setSelectedEdge(edge);
                  setSelectedNode(null);
                }}
              />
            )}

            {/* Legend */}
            <div className="px-4 py-3 border-t border-khiimori-border/10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {Object.entries(edgeTypeColors).map(([type, color]) => (
                <div key={type} className="flex items-center gap-2 leading-none">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-khiimori-text-secondary text-xs">{edgeTypeLabels[type as keyof typeof edgeTypeLabels]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Selected Node/Edge Details */}
            {selectedNode && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-5 border border-khiimori-border/10"
              >
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="p-2.5 bg-primary-500/20 rounded-xl shrink-0">
                      <Globe className="w-5 h-5 text-primary-400" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-khiimori-text-primary font-semibold text-sm leading-snug line-clamp-2">
                        {safeStr(selectedNode.label)}
                      </h3>
                      {selectedNode.url && (
                        <p
                          className="text-khiimori-text-muted text-xs mt-1 line-clamp-2 break-all leading-relaxed"
                          title={selectedNode.url}
                        >
                          {selectedNode.url}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button onClick={() => setSelectedNode(null)} variant="ghost" size="sm" className="shrink-0" aria-label="Close node details">
                    <X className="w-4 h-4 text-khiimori-text-muted" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {(selectedNode.topics ?? []).length > 0 && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted mb-2">Topics</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(selectedNode.topics ?? []).map((topic, i) => (
                          <span key={i} className="px-2 py-0.5 bg-primary-500/20 text-primary-300 rounded-full text-xs leading-normal">
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted mb-0">Sentiment</p>
                    <MetricBar value={selectedNode.sentiment || 0} />
                  </div>

                  <div className="flex items-stretch gap-2 pt-1">
                    {selectedNode.url && (
                      <Button
                        onClick={() => window.open(selectedNode.url, '_blank', 'noopener,noreferrer')}
                        variant="primary"
                        className="flex-1"
                      >
                        Open Tab
                      </Button>
                    )}
                    <Button
                      onClick={() => openEditNodeModal(selectedNode)}
                      variant="glass"
                      size="md"
                      className="shrink-0 px-3"
                      aria-label="Edit node"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => openDeleteConfirm(selectedNode.id, 'node')}
                      variant="glass"
                      size="md"
                      className="shrink-0 px-3"
                      aria-label="Delete node"
                    >
                      <Trash2 className="w-4 h-4 text-error-400" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {selectedEdge && !selectedNode && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-5 border border-khiimori-border/10"
              >
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: edgeTypeColors[selectedEdge.type] }} />
                    <h3 className="text-khiimori-text-primary font-semibold text-sm capitalize truncate">
                      {selectedEdge.type} connection
                    </h3>
                  </div>
                  <Button onClick={() => setSelectedEdge(null)} variant="ghost" size="sm" className="shrink-0" aria-label="Close edge details">
                    <X className="w-4 h-4 text-khiimori-text-muted" />
                  </Button>
                </div>

                <div className="space-y-2.5 mb-4">
                  <DetailRow label="From">
                    <span className="line-clamp-2 leading-snug">
                      {safeStr(nodes.find(n => n.id === selectedEdge.source)?.label)}
                    </span>
                  </DetailRow>
                  <DetailRow label="To">
                    <span className="line-clamp-2 leading-snug">
                      {safeStr(nodes.find(n => n.id === selectedEdge.target)?.label)}
                    </span>
                  </DetailRow>
                  {selectedEdge.label && (
                    <DetailRow label="Label">
                      <span className="leading-snug">{safeStr(selectedEdge.label)}</span>
                    </DetailRow>
                  )}
                </div>

                <div className="mb-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted">Strength</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 h-2 bg-khiimori-bg-elevated/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary-500 to-purple-500 rounded-full"
                        style={{ width: `${selectedEdge.strength * 100}%` }}
                      />
                    </div>
                    <span className="text-khiimori-text-primary text-sm w-14 text-right shrink-0 leading-none tabular-nums">
                      {getEdgeStrengthLabel(selectedEdge.strength)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={() => openDeleteConfirm(selectedEdge.id, 'edge')}
                  variant="glass"
                  className="w-full"
                  leftIcon={<Trash2 className="w-4 h-4" />}
                >
                  Delete Connection
                </Button>
              </motion.div>
            )}

            {!selectedNode && !selectedEdge && (
              <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-5 border border-khiimori-border/10">
                <p className="text-khiimori-text-muted text-sm text-center leading-relaxed">
                  Click a node or connection to view details
                </p>
              </div>
            )}

            {/* AI Insights */}
            {showInsights && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-5 border border-khiimori-border/10"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-4 h-4 text-warning-400 shrink-0" />
                  <h3 className="text-khiimori-text-primary font-semibold text-sm leading-none">AI Insights</h3>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {insights.length === 0 ? (
                    <p className="text-khiimori-text-muted text-sm leading-relaxed">
                      Connections will appear here after tabs are synced.
                    </p>
                  ) : null}
                  {insights.map((insight) => (
                    <button
                      key={insight.id}
                      type="button"
                      onClick={() => {
                        const edge = edges.find((e) => e.id === insight.id);
                        if (edge) {
                          setSelectedEdge(edge);
                          setSelectedNode(null);
                        }
                      }}
                      className="w-full text-left p-3 rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 hover:bg-khiimori-bg-elevated/10 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: edgeTypeColors[insight.type as keyof typeof edgeTypeColors] }}
                        />
                        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-2 items-center flex-1 min-w-0">
                          <span
                            className="text-primary-300 text-xs truncate text-right leading-tight"
                            title={insight.sourceLabel}
                          >
                            {shortTabLabel(insight.sourceLabel)}
                          </span>
                          <span className="text-khiimori-text-muted text-xs shrink-0 leading-none">→</span>
                          <span
                            className="text-primary-300 text-xs truncate text-left leading-tight"
                            title={insight.targetLabel}
                          >
                            {shortTabLabel(insight.targetLabel)}
                          </span>
                        </div>
                      </div>
                      <p className="text-khiimori-text-secondary text-xs leading-relaxed pl-4">
                        {safeStr(insight.insight)}
                      </p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Create Node Modal */}
      <Modal
        isOpen={showCreateNodeModal}
        onClose={() => setShowCreateNodeModal(false)}
        title="Add Node"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Label *"
            value={nodeFormData.label}
            onChange={(e) => setNodeFormData(prev => ({ ...prev, label: e.target.value }))}
            placeholder="Node label"
          />
          <Input
            label="URL"
            type="url"
            value={nodeFormData.url}
            onChange={(e) => setNodeFormData(prev => ({ ...prev, url: e.target.value }))}
            placeholder="https://example.com"
          />
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Topics</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {nodeFormData.topics.map(topic => (
                <span key={topic} className="flex items-center gap-1 px-2 py-1 bg-primary-500/20 text-primary-300 rounded-lg text-sm">
                  {topic}
                  <Button onClick={() => handleRemoveTopic(topic)} variant="ghost" size="sm" aria-label={`Remove topic ${topic}`}>
                    <X className="w-3 h-3" />
                  </Button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTopic())}
                placeholder="Add topic"
              />
              <Button onClick={handleAddTopic} variant="glass">
                Add
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Sentiment: {Math.round(nodeFormData.sentiment * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={nodeFormData.sentiment}
              onChange={(e) => setNodeFormData(prev => ({ ...prev, sentiment: parseFloat(e.target.value) }))}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={() => setShowCreateNodeModal(false)}
            variant="glass"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={createNode}
            disabled={!nodeFormData.label}
            variant="primary"
            className="flex-1"
            leftIcon={<Save className="w-4 h-4" />}
          >
            Create
          </Button>
        </div>
      </Modal>

      {/* Edit Node Modal */}
      <Modal
        isOpen={showEditNodeModal}
        onClose={() => setShowEditNodeModal(false)}
        title="Edit Node"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Label *"
            value={nodeFormData.label}
            onChange={(e) => setNodeFormData(prev => ({ ...prev, label: e.target.value }))}
          />
          <Input
            label="URL"
            type="url"
            value={nodeFormData.url}
            onChange={(e) => setNodeFormData(prev => ({ ...prev, url: e.target.value }))}
            readOnly
          />
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Topics</label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {nodeFormData.topics.map(topic => (
                <span key={topic} className="flex items-center gap-1 px-2 py-1 bg-primary-500/20 text-primary-300 rounded-lg text-sm">
                  {topic}
                  <Button onClick={() => handleRemoveTopic(topic)} variant="ghost" size="sm" aria-label={`Remove topic ${topic}`}>
                    <X className="w-3 h-3" />
                  </Button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTopic())}
                placeholder="Add topic"
              />
              <Button onClick={handleAddTopic} variant="glass">
                Add
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Sentiment: {Math.round(nodeFormData.sentiment * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={nodeFormData.sentiment}
              onChange={(e) => setNodeFormData(prev => ({ ...prev, sentiment: parseFloat(e.target.value) }))}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={() => setShowEditNodeModal(false)}
            variant="glass"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={() => updateNode(selectedNode!.id)}
            variant="primary"
            className="flex-1"
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save
          </Button>
        </div>
      </Modal>

      {/* Create Edge Modal */}
      <Modal
        isOpen={showCreateEdgeModal}
        onClose={() => setShowCreateEdgeModal(false)}
        title="Add Connection"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">From Node</label>
            <select
              value={edgeFormData.sourceId}
              onChange={(e) => setEdgeFormData(prev => ({ ...prev, sourceId: e.target.value }))}
              className="w-full px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-primary-500/50"
            >
              <option value="">Select node...</option>
              {nodes.map(node => (
                <option key={node.id} value={node.id}>{safeStr(node.label)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">To Node</label>
            <select
              value={edgeFormData.targetId}
              onChange={(e) => setEdgeFormData(prev => ({ ...prev, targetId: e.target.value }))}
              className="w-full px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-primary-500/50"
            >
              <option value="">Select node...</option>
              {nodes.filter(n => n.id !== edgeFormData.sourceId).map(node => (
                <option key={node.id} value={node.id}>{safeStr(node.label)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Type</label>
            <select
              value={edgeFormData.type}
              onChange={(e) => setEdgeFormData(prev => ({ ...prev, type: e.target.value as MeshEdge['type'] }))}
              className="w-full px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-primary-500/50"
            >
              <option value="semantic">Semantic (Content similarity)</option>
              <option value="temporal">Temporal (Time-based)</option>
              <option value="referential">Referential (Direct reference)</option>
              <option value="contextual">Contextual (Related context)</option>
            </select>
          </div>
          <div>
            <label className="block text-khiimori-text-secondary text-sm mb-2">Strength: {Math.round(edgeFormData.strength * 100)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={edgeFormData.strength}
              onChange={(e) => setEdgeFormData(prev => ({ ...prev, strength: parseFloat(e.target.value) }))}
              className="w-full"
            />
          </div>
          <Input
            label="Label (optional)"
            value={edgeFormData.label}
            onChange={(e) => setEdgeFormData(prev => ({ ...prev, label: e.target.value }))}
            placeholder="Connection description"
          />
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={() => setShowCreateEdgeModal(false)}
            variant="glass"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={createEdge}
            disabled={!edgeFormData.sourceId || !edgeFormData.targetId}
            variant="primary"
            className="flex-1"
            leftIcon={<Link2 className="w-4 h-4" />}
          >
            Create
          </Button>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleteType === 'node' ? 'Node' : 'Connection'}?`}
        description={`This action cannot be undone${deleteType === 'node' ? '. All connected edges will also be removed.' : '.'}`}
        confirmLabel="Delete"
        confirmVariant="danger"
      />
    </PageLayout>
  );
}